import React from "react";
import { Loader2, Sparkles } from "lucide-react";

function StatusLine({ site, enabled, hasRules }) {
  if (enabled) return <span className="text-sm">Email routing is <b>active</b> on {site.domain}</span>;
  if (hasRules) return <span className="text-sm">Email routing is <b>active</b> on {site.domain} <span className="text-zinc-500">(status flag unreadable via API, but rules are configured — see note below)</span></span>;
  return <span className="text-sm text-amber-300">Rules configured · <b>finish enable in CF Dashboard</b> to start receiving mail</span>;
}

function ManualEnableNotice({ domain }) {
  return (
    <div className="mb-4 text-[11px] mono text-amber-300/80 leading-relaxed border border-amber-500/20 bg-amber-500/5 rounded-sm p-3">
      <div className="font-semibold mb-1">One-time manual step required (Cloudflare API limitation):</div>
      <ol className="list-decimal pl-4 space-y-0.5">
        <li>Open <a href={`https://dash.cloudflare.com/?to=/:account/${domain}/email/routing/overview`} target="_blank" rel="noreferrer" className="underline text-amber-200 hover:text-amber-100">Cloudflare Dashboard → {domain} → Email → Email Routing</a></li>
        <li>Click the <b>&quot;Enable Email Routing&quot;</b> button — CF auto-creates MX/SPF/DMARC records</li>
        <li>Refresh this page — status will turn green</li>
      </ol>
      <div className="mt-2 text-[10px] opacity-70">Why: CF&apos;s `enable` API endpoint requires a permission scope they don&apos;t expose via tokens. After this one-time click, all subsequent address operations work via API.</div>
    </div>
  );
}

function FlagUnreadableNotice() {
  return (
    <div className="mb-4 text-[11px] mono text-emerald-300/90 leading-relaxed border border-emerald-500/20 bg-emerald-500/5 rounded-sm p-3">
      <div className="font-semibold mb-1">Routing is active on this zone.</div>
      <div className="opacity-80">CF&apos;s <code>settings.enabled</code> flag isn&apos;t readable with our current token scope, so we can&apos;t show the green dot — but rules exist and forward via API, which means Email Routing is enabled. Send a test email to any address below to confirm.</div>
    </div>
  );
}

/** Not-yet-enabled card with the bootstrap button. */
export function BootstrapCard({ busy, disabled, onBootstrap }) {
  return (
    <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-5">
      <div className="label-tiny mb-2">Status</div>
      <div className="flex items-center gap-3"><span className="dot bg-zinc-600" /><span className="text-sm text-zinc-400">Email routing not enabled on this zone</span></div>
      <button onClick={onBootstrap} disabled={busy || disabled} className="mt-4 inline-flex items-center gap-2 bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-50 px-4 py-2 rounded-sm text-sm font-semibold">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Enable + bootstrap default addresses
      </button>
      <div className="mt-2 text-[11px] text-zinc-500 font-mono">Creates MX records on the zone, then adds: app@, contact@, newsroom@, advertise@, tips@, info@, hello@</div>
    </div>
  );
}

/** Active-routing card: status line, one-time notices and the catch-all toggle. */
export function RoutingStatusCard({ site, overview, adminDest, busy, onToggleCatchAll }) {
  const enabled = overview.status?.enabled;
  const hasRules = (overview.addresses || []).length > 0;
  const n = overview.addresses.length;
  return (
    <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-5">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <span className={`dot ${enabled || hasRules ? "bg-emerald-500" : "bg-amber-500"}`} />
        <StatusLine site={site} enabled={enabled} hasRules={hasRules} />
        <div className="ml-auto text-[11px] mono text-zinc-500">{n} address{n === 1 ? "" : "es"}</div>
      </div>
      {!enabled && !hasRules && <ManualEnableNotice domain={site.domain} />}
      {!enabled && hasRules && <FlagUnreadableNotice />}
      <label className="flex items-center gap-3 cursor-pointer p-3 -mx-3 border border-zinc-800 rounded-sm">
        <input type="checkbox" checked={!!overview.catch_all?.enabled} onChange={(e) => onToggleCatchAll(e.target.checked)} disabled={busy || !enabled} />
        <div className="flex-1">
          <div className="text-sm text-zinc-200">Catch-all</div>
          <div className="text-[11px] text-zinc-500 font-mono">Any address @{site.domain} → {adminDest}</div>
        </div>
      </label>
    </div>
  );
}
